import React, { useEffect, useState } from 'react';

// Stack of undo toasts shown at the bottom of the screen.
// Each toast auto-dismisses after `ttlMs` (default 6s) unless the user
// clicks "undo" (if present) or taps the message body (if the toast
// carries an `onClick` action — see the morning reminder summary in
// BoardPage as the canonical example).
export default function UndoToastStack({ toasts, onUndo, onDismiss, ttlMs = 6000 }) {
  // Walter 2026-05-21: on mobile the soft keyboard overlays the layout
  // viewport without shrinking it (iOS especially), so a toast pinned
  // to `bottom: 18px` sits *behind* the keyboard — e.g. the
  // new-reminder confirmation toast was unreadable while typing.
  // Track window.visualViewport and lift the stack by however much
  // the keyboard (or other browser UI) is covering at the bottom.
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      // Bottom area covered = layout viewport height − visual
      // viewport height − how far the visual viewport is scrolled.
      // On Android (layout viewport shrinks with the keyboard) this
      // stays ~0, which is correct — bottom:18px already clears it.
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(inset);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return (
    <div style={{
      position: 'fixed',
      bottom: `calc(18px + ${keyboardInset}px)`,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 2500,
      display: 'flex', flexDirection: 'column', gap: '6px',
      alignItems: 'center',
      pointerEvents: 'none',
      // Glide up with the keyboard rather than jumping.
      transition: 'bottom 0.2s ease',
    }}>
      {toasts.map(t => (
        <Toast
          key={t.id}
          toast={t}
          ttlMs={t.ttlMs ?? ttlMs}
          onUndo={() => onUndo(t.id)}
          onDismiss={() => onDismiss(t.id)}
        />
      ))}
    </div>
  );
}

function Toast({ toast, ttlMs, onUndo, onDismiss }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, ttlMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  // Walter 2026-05-15: toasts can now carry an `onClick` that fires
  // when the user taps the message body (separate from `undoAction`,
  // which is the explicit undo button). When present, the toast reads
  // as a clickable card — pointer cursor + hover bg + a subtle "→"
  // affordance on the right.
  const clickable = typeof toast.onClick === 'function';
  const fireClick = (e) => {
    // Don't double-fire if the user actually tapped the close × or the
    // undo button (those have their own handlers; stopPropagation in
    // those buttons would be cleaner but is more invasive).
    if (e.target.closest('button[data-toast-control]')) return;
    if (!clickable) return;
    toast.onClick();
    onDismiss();
  };

  return (
    <div
      onClick={clickable ? fireClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fireClick(e); } } : undefined}
      style={{
        pointerEvents: 'auto',
        background: 'var(--surface)',
        border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-toast)',
        display: 'flex', alignItems: 'center',
        fontFamily: 'var(--mono)', fontSize: '12px',
        color: 'var(--text)',
        padding: '10px 14px',
        gap: '14px',
        animation: 'lists-fadein 0.15s ease',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 0.15s, border-color 0.15s',
        // Cap the width so messages don't expand the toast off-screen
        // on narrow viewports — and let the inner message ellipsis when
        // it overflows. Min stays sensible so a 5-word toast doesn't
        // look pinched.
        minWidth: 'min(260px, calc(100vw - 32px))',
        maxWidth: 'min(420px, calc(100vw - 32px))',
      }}
      onMouseEnter={clickable ? (e) => {
        e.currentTarget.style.background = 'var(--surface2)';
        e.currentTarget.style.borderColor = 'var(--accent-dim)';
      } : undefined}
      onMouseLeave={clickable ? (e) => {
        e.currentTarget.style.background = 'var(--surface)';
        e.currentTarget.style.borderColor = 'var(--border-bright)';
      } : undefined}
    >
      <span style={{
        flex: 1, color: 'var(--text-dim)',
        // Allow long messages (size-limit warnings, reminder confirms
        // with snippets) to wrap to a second line rather than truncate
        // with an ellipsis. Word-break on long pasted content keeps a
        // single rogue word from blowing past maxWidth.
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.4,
        minWidth: 0,
      }}>{toast.message}</span>
      {/* Subtle chevron affordance — visible only when the toast is
          clickable, so users learn the gesture without it competing
          with the undo / dismiss buttons. */}
      {clickable && !toast.undoAction && (
        <span aria-hidden="true" style={{
          color: 'var(--accent)',
          fontSize: '14px',
          lineHeight: 1,
          flex: 'none',
        }}>→</span>
      )}
      {toast.undoAction && (
        <button
          data-toast-control
          onClick={(e) => { e.stopPropagation(); onUndo(); }}
          style={{
            background: 'none',
            border: '1px solid var(--accent-dim)',
            color: 'var(--accent)',
            fontFamily: 'var(--mono)', fontSize: '11px',
            padding: '4px 10px',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.color = '#000'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--accent)'; }}
        >
          undo
        </button>
      )}
      <button
        data-toast-control
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        title="dismiss"
        style={{
          background: 'none', border: 'none',
          color: 'var(--text-dimmer)',
          cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center',
        }}
        onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}
